const _ = require('lodash');

/**
 * Price selection logic shared between the React storefront (so the displayed product price is
 * consistent across components) and the outbound Meta DPA catalog feed (so the price Meta sees
 * matches the price the shopper will see on the landing page).
 *
 * @typedef {Object} VariantLike
 * @property {number} [price]    Numeric SKU price.
 * @property {string} [rawTitle] Raw variant title — used to detect non-standard sizes like "mini" or "travel".
 * @property {string} [retailer] Retailer name — used to dedupe across retailers in getVisibleVariantsForProduct.
 *
 * @typedef {Object} UrlDataLike
 * @property {number} [productPrice] Live-crawled URL price (the freshest signal we have).
 *
 * @typedef {Object} LinkLike
 * @property {UrlDataLike} [url_data]
 *
 * @typedef {Object} ProductLike
 * @property {VariantLike[]} [variants]
 * @property {LinkLike[]}    [links]
 * @property {number}        [fallbackPrice]
 */

/**
 * True for variants whose rawTitle suggests a non-standard size (mini, travel, value/maxi size).
 * Those variants are deprioritized in the displayed price so we don't accidentally surface a
 * "travel size" price as the headline price for a standard product.
 *
 * @param {VariantLike} variant
 * @returns {boolean}
 */
const isNonStandardSize = variant => {
  const adjUrl = variant.rawTitle ? variant.rawTitle.toLowerCase() : '';
  return (
    adjUrl.includes(' mini') || adjUrl.includes('mini ') || adjUrl.includes('value size') || adjUrl.includes('maxi size') || adjUrl.includes('travel')
  );
};

/**
 * Returns one variant per retailer (ordered by isNonStandardSize, then retailer name).
 * This is what the storefront iterates to render the "available at N retailers" list.
 *
 * @param {ProductLike} product
 * @returns {VariantLike[]}
 */
const getVisibleVariantsForProduct = product => {
  const { variants } = product || {};
  const orderedVariants = _.orderBy(variants, [isNonStandardSize, v => (v.retailer || '').toLowerCase()]);
  return _.uniqBy(orderedVariants, 'retailer');
};

/**
 * Picks the price to show for a product, in this order of preference:
 *   1) Lowest live-crawled price across links (links[*].url_data.productPrice) — freshest signal,
 *      matches what the "Shop Now" button shows on the storefront
 *   2) Standard-size price that repeats 3+ times across variants (the "modal" price)
 *   3) Lowest visible-variant price (variants deduped per retailer, non-standard sizes deprioritized)
 *   4) product.fallbackPrice
 *
 * @param {ProductLike} product
 * @returns {number|null|undefined}
 */
const getPriceForProduct = product => {
  const { fallbackPrice, variants, links } = product || {};

  // (1) Lowest live URL-crawled price — wins when present because it's the price the user will
  // actually pay at the retailer (i.e. the "Shop Now" button price).
  const livePrices = _(links)
    .map(l => l?.url_data?.productPrice)
    .filter(p => typeof p === 'number' && p > 0)
    .value();
  if (livePrices.length) return Math.min(...livePrices);

  // (2)/(3) Existing variant-based selection.
  const visibleVariants = getVisibleVariantsForProduct(product);
  const orderedVariants = _.orderBy(visibleVariants, [isNonStandardSize, 'price'], ['asc', 'asc']);

  const counts = _.countBy(
    _.filter(variants, v => !isNonStandardSize(v)),
    'price'
  );
  const repeatedPrice = parseFloat(_.reduce(counts, (r, v, k) => (v > 2 && r[1] < v ? [k, v] : r), [0, 0])[0]);

  const bestPrice = _.get(orderedVariants, '0.price');

  // (4) fallbackPrice
  return repeatedPrice || bestPrice || fallbackPrice;
};

module.exports = {
  isNonStandardSize,
  getVisibleVariantsForProduct,
  getPriceForProduct
};
